import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import ReactPopup from 'reactjs-popup';
import { ColorRing } from 'react-loader-spinner';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

import SetMapRef from './hooks/SetMapRef';
import CustomMarker from './components/CustomMarker';
import ClientSidebar from './components/ClientDashboardComps/ClientSidebar';
import FilterDrawer from './components/FilterDrawer/FilterDrawer';
import { useFilterDrawerStore } from './components/FilterDrawer/useFilterDrawerStore';

import { getIsServicePorviderExists } from 'api/clients/getIsServicePorviderExists';
import { GroupedObject, ObjectItem } from 'api/clients/types';
import ManageObjectModalV2 from 'components/ClientTable/Modals/ManageObjectModalV2/ManageObjectModalV2';
import PutchEmailNotifyModal from 'components/ClientTable/Modals/NotifyModal/PutchEmailNotifyModal';
import DeleteObjModalV2 from 'components/ClientTable/Modals/DeleteObjModal/DeleteObjModalV2';
import 'leaflet/dist/leaflet.css';
import AddTechnicianModal from 'components/ClientTable/Modals/AddTechnicanModal/AddTechnicianModal';
import DeleteTechnicianModal from 'components/ClientTable/Modals/DeleteTechnicianModal/DeleteTechnicianModal';

export interface MarkerData {
  clientId: number;
  clientName: string;
  isDraggable: boolean;
  object_data: ObjectItem;
}

interface Props {
  data: GroupedObject[];
  refetch: () => void;
  isLoading: boolean;
  isRefetching: boolean;
}

const ClientDashboardV2: React.FC<Props> = ({ data, refetch, isLoading, isRefetching }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'ClientDashboardV2' });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mapCenter, setMapCenter] = useState<[number, number]>([51.505, -0.09]);

  const [markers, setMarkers] = useState<MarkerData[]>([]);
  const [selectedMarkerPosition, setSelectedMarkerPosition] = useState<[number, number] | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [justSetToMapId, setJustSetToMapId] = useState<number | null>(null);
  const markerRefs = useRef<Record<number, any>>({});

  const { appliedClientFilter, activityFilter } = useFilterDrawerStore();

  const [mapHeight, setMapHeight] = useState<number>(window.innerHeight - 200);
  const [initialCenterSet, setInitialCenterSet] = useState(false);

  const mapRef = useRef<any>(null);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const popupRef = useRef<any>(null);
  const [isCompanyInactive] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isCompanyInactive) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const pollServiceProvider = async () => {
      try {
        const { is_pest_company, is_active } = await getIsServicePorviderExists();

        if (is_pest_company && is_active) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Polling each 5 seconds
    pollingIntervalRef.current = setInterval(pollServiceProvider, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isCompanyInactive]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const handleResize = () => {
      setMapHeight(window.innerHeight - 200);
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!data) return;

    const newMarkers: MarkerData[] = data.flatMap((client: GroupedObject) =>
      client.object_list.map(obj => ({
        clientId: client.id,
        clientName: client.name,
        isDraggable: false,
        object_data: obj,
      })),
    );

    setMarkers(newMarkers);

    if (newMarkers.length > 0 && newMarkers[0].object_data.position && !initialCenterSet && mapRef.current) {
      mapRef.current.setView(newMarkers[0].object_data.position, mapRef.current.getZoom());
      setInitialCenterSet(true);
    }
  }, [data, initialCenterSet]);

  // Zoom
  const handleZoomToMarker = (position: [number, number]) => {
    setSelectedMarkerPosition(position);
  };

  const handleSetOnMap = (obj: ObjectItem): void => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const newPos: [number, number] = [center.lat, center.lng];
      setMarkers(prev =>
        prev.map(m =>
          m.object_data.id === obj.id
            ? { ...m, object_data: { ...m.object_data, position: newPos }, isDraggable: true }
            : m,
        ),
      );

      setJustSetToMapId(obj.id);
    }
  };

  const handleMarkerDragEnd = (objData: ObjectItem, newPos: [number, number]) => {
    setMarkers(prev =>
      prev.map(m =>
        m.object_data.id === objData.id ? { ...m, object_data: { ...m.object_data, position: newPos } } : m,
      ),
    );
  };

  const handleToggleDraggable = (objData: ObjectItem) => {
    setMarkers(prev => prev.map(m => (m.object_data.id === objData.id ? { ...m, isDraggable: !m.isDraggable } : m)));
  };

  const handleDisableDraggable = (objData: ObjectItem) => {
    setMarkers(prev => prev.map(m => (m.object_data.id === objData.id ? { ...m, isDraggable: false } : m)));
    setForceUpdate(prev => prev + 1);
    setJustSetToMapId(null);
  };

  useEffect(() => {
    if (justSetToMapId !== null) {
      const timer = setTimeout(() => {
        const marker = markerRefs.current[justSetToMapId];
        if (marker && typeof marker.openPopup === 'function') {
          marker.openPopup();
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [justSetToMapId, forceUpdate]);

  const filteredMarkers = markers.filter(m => {
    const clientMatch = appliedClientFilter.length === 0 || appliedClientFilter.includes(m.clientId);
    const activityMatch = activityFilter === null || m.object_data.is_activity === activityFilter;
    return clientMatch && activityMatch;
  });

  const uniqueClientMap: Record<number, string> = {};
  markers.forEach(m => {
    uniqueClientMap[m.clientId] = m.clientName;
  });

  const handleClosePopup = () => {
    if (popupRef.current) {
      popupRef.current.close();
    }
  };
  const createClusterCustomIcon = (cluster: any) => {
    const markers = cluster.getAllChildMarkers();

    const hasActiveMarker = markers.some((marker: any) => marker.options.isActivity === 'danger');

    let cssClass = 'marker-cluster ';

    if (hasActiveMarker) {
      cssClass += 'marker-custom-color-red';
    } else {
      cssClass += 'marker-cluster-small';
    }

    return L.divIcon({
      html: `<div><span>${markers.length}</span></div>`,
      className: cssClass,
      iconSize: L.point(40, 40, true),
    });
  };
  return (
    <>
      <div className="card-body p-0">
        <div className="row g-0">
          <div className="col-xxl-12 position-relative">
            {(isLoading || isRefetching) && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1001,
                }}
              >
                <ColorRing
                  visible
                  height="80"
                  width="80"
                  ariaLabel="color-ring-loading"
                  wrapperStyle={{}}
                  wrapperClass="color-ring-wrapper"
                  colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
                />
              </div>
            )}
            <div className="position-absolute top-0 end-0 mx-2" style={{ zIndex: 12 }}>
              <ReactPopup
                ref={popupRef}
                trigger={
                  <button type="button" className="map-dropdown-toggle" style={{ position: 'relative', marginTop: 10 }}>
                    {t('openNavigation')}
                  </button>
                }
                position="bottom right"
                arrow={false}
                offsetY={10}
                closeOnDocumentClick={false}
                closeOnEscape={false}
                contentStyle={{
                  maxHeight: 'calc(100vh - 200px)',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxWidth: '400px',
                  width: windowWidth >= 430 ? '100%' : '80%',
                }}
              >
                <ClientSidebar
                  appliedClientFilter={appliedClientFilter}
                  markers={markers}
                  handleZoomToMarker={handleZoomToMarker}
                  handleSetOnMap={handleSetOnMap}
                  onClose={handleClosePopup}
                />
              </ReactPopup>
            </div>

            <MapContainer
              center={mapCenter}
              zoom={7}
              scrollWheelZoom
              style={{ height: `${windowWidth > 430 ? `${mapHeight}px` : '100vh'}`, width: '100%', zIndex: 10 }}
              className="rounded-0"
            >
              <TileLayer attribution={t('osmContributors')} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <SetMapRef mapRef={mapRef} />
              {selectedMarkerPosition && <ZoomToMarker position={selectedMarkerPosition} />}

              <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterCustomIcon}>
                {filteredMarkers.map(m => (
                  <CustomMarker
                    key={`${m.object_data.id}-${forceUpdate}`}
                    object_data={m.object_data}
                    isActivity={m.object_data.activity_status}
                    isDraggable={m.isDraggable}
                    onDragEnd={handleMarkerDragEnd}
                    onToggleDraggable={handleToggleDraggable}
                    onSavePositionSuccess={handleDisableDraggable}
                    markerRef={(ref: any) => {
                      if (ref) {
                        markerRefs.current[m.object_data.id] = ref;
                      }
                    }}
                  />
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          </div>
        </div>
      </div>
      <ManageObjectModalV2 handleZoomToMarker={handleZoomToMarker} />

      <PutchEmailNotifyModal refetchClients={refetch} />
      <DeleteObjModalV2 />
      <FilterDrawer />
      <AddTechnicianModal refetchClients={refetch} />
      <DeleteTechnicianModal refetch={refetch} />
    </>
  );
};

const ZoomToMarker = ({ position }: { position: [number, number] }) => {
  const map = useMap();
  React.useEffect(() => {
    map.flyTo(position, 15, { duration: 0.5 });
  }, [position, map]);
  return null;
};

export default ClientDashboardV2;
